// Step configuration - defines order, components, and API mappings
// Reordering steps is as simple as moving objects in this array

import { isOnlyPlatformBaseUrl } from '../utils/urlUtils';

export const WIZARD_STEPS = [
  {
    id: 'company',
    component: 'CompanyDetails',
    title: 'Company Selection',
    dataKey: 'company',
    apiConfig: {
      endpoint: '/v3/advertising/step/{couponId}/company',
      method: 'PATCH',
      payload: (wizardData) => {
        const company = wizardData.company;
        const payload = {
          company_name: company.name,
          background_color: company.backgroundColor || '#FFFFFF',
          email_address: company.email,
          phone_number: company.managerNumber,
          description: company.description,
          contact_person: company.managerName,
          website: company.website
        };
        if (company.facebookPage && !isOnlyPlatformBaseUrl(company.facebookPage, 'facebook')) {
          payload.facebook_page = company.facebookPage;
        }
        if (company.instagramProfile && !isOnlyPlatformBaseUrl(company.instagramProfile, 'instagram')) {
          payload.instagram_profile = company.instagramProfile;
        }
        if (company.pinterestProfile && !isOnlyPlatformBaseUrl(company.pinterestProfile, 'pinterest')) {
          payload.pinterest_profile = company.pinterestProfile;
        }
        return payload;
      }
    },
    skipWhen: (wizardData) => !!wizardData.company.companyId // Skip to step 3 for existing company
  },
  {
    id: 'logo',
    component: 'LogoUpload',
    title: 'Company Logo',
    dataKey: 'logo',
    apiConfig: null // Logo upload handled in component
  },
  {
    id: 'deal-selection',
    component: 'ChooseDeals',
    title: 'Choose Deals',
    dataKey: 'dealSelection',
    apiConfig: null // Stored in context only
  },
  {
    id: 'coupon-type',
    component: 'CouponType',
    title: 'Coupon Type',
    dataKey: 'couponType',
    apiConfig: {
      endpoint: '/v3/advertising/step/{couponId}/deal',
      method: 'PATCH',
      payload: (wizardData) => ({
        deal: wizardData.couponType.deal,
        dealUnit: wizardData.couponType.dealUnit,
        dealItem: wizardData.couponType.dealItem
      })
    }
  },
  {
    id: 'expiration',
    component: 'CouponExpiration',
    title: 'Coupon Expiration',
    dataKey: 'expiration',
    apiConfig: {
      endpoint: '/v3/advertising/step/{couponId}/cost',
      method: 'PATCH',
      payload: (wizardData) => ({
        durationType: wizardData.expiration.durationType,
        duration: wizardData.expiration.duration
      })
    }
  },
  {
    id: 'coupon-details',
    component: 'CouponCodeAndDetails',
    title: 'Coupon Code & Details',
    dataKey: 'couponDetails',
    apiConfig: {
      endpoint: '/v3/advertising/step/{couponId}/details',
      method: 'PATCH',
      payload: (wizardData) => ({
        couponCode: wizardData.couponDetails?.code || '',
        couponWebsite: wizardData.couponDetails?.website || '',
        couponPhone: wizardData.couponDetails?.phone || '',
        couponEmail: wizardData.couponDetails?.email || '',
        couponDescription: wizardData.couponDetails?.description || ''
      })
    }
  },
  {
    id: 'terms',
    component: 'TermsAndConditions',
    title: 'Terms & Conditions',
    dataKey: 'terms',
    apiConfig: {
      endpoint: '/v3/advertising/step/{couponId}/terms',
      method: 'PATCH',
      payload: (wizardData) => ({
        terms: wizardData.terms.terms
      })
    }
  },
  {
    id: 'instructions',
    component: 'Instructions',
    title: 'Instructions',
    dataKey: 'terms', // Same dataKey as terms (both stored in terms object)
    apiConfig: {
      endpoint: '/v3/advertising/step/{couponId}/terms',
      method: 'PATCH',
      payload: (wizardData) => ({
        instructions: wizardData.terms.instructions
      })
    }
  },
  {
    id: 'locations',
    component: 'Locations',
    title: 'Location',
    dataKey: 'locations',
    apiConfig: {
      endpoint: '/v3/advertising/step/{couponId}/locations',
      method: 'PATCH',
      payload: (wizardData) => {
        const sanitizedLocations = (wizardData.locations?.list || []).map(loc => ({
          title: loc.title,
          address: loc.address,
          latitude: loc.latitude,
          longitude: loc.longitude
        }));
        return {
          locations: sanitizedLocations,
          isOnlineOnly: wizardData.locations?.isOnlineOnly || false,
          countries: wizardData.locations?.countries || [],
          isGlobal: wizardData.locations?.isGlobal || false
        };
      }
    }
  },
  {
    id: 'categories',
    component: 'Categories',
    title: 'Categories',
    dataKey: 'categories',
    apiConfig: {
      endpoint: '/v3/advertising/step/{couponId}/categories',
      method: 'PATCH',
      payload: (wizardData) => ({
        categories: wizardData.categories.selected.map(cat => cat.id)
      })
    }
  }
];

// Helper function to get step by index (1-based)
export const getStepByIndex = (index) => {
  return WIZARD_STEPS[index - 1] || null;
};

// Helper function to get step by id
export const getStepById = (id) => {
  return WIZARD_STEPS.find(step => step.id === id) || null;
};

// Helper function to get total step count
export const getTotalSteps = () => {
  return WIZARD_STEPS.length;
};
